import { ActionIcon, CloseButton, TextInput, TextInputProps } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import AbsoluteError from 'components/ui/AbsoluteError';
import { useScrollFocusedFieldOnKeyboardResize } from 'hooks/useScrollFocusedFieldOnKeyboardResize';
import { FieldTextStore } from 'stores/local/shared/form/FieldTextStore';
import { useDevices } from 'utils/devices';
import { preventDefault } from 'utils/preventDefault';

export type FieldTextProps = Omit<TextInputProps, 'value' | 'onChange' | 'disabled' | 'error' | 'withAsterisk'> & {
  store: FieldTextStore;
  withClear?: boolean;
  withCloseButton?: boolean;
  disabled?: boolean;
  validateOnChange?: boolean;
  digitsOnly?: boolean;
};

const FieldText: React.FC<FieldTextProps> = ({
  store,
  withClear = false,
  rightSection,
  withCloseButton = false,
  disabled = false,
  validateOnChange = false,
  digitsOnly = false,
  ...props
}) => {
  const { inputRef, handleFocus } = useScrollFocusedFieldOnKeyboardResize(store);

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = digitsOnly ? e.target.value.replace(/\D/g, '') : e.target.value;

      store.setValue(nextValue);

      if (validateOnChange) {
        store.validate();
      }
    },
    [digitsOnly, store, validateOnChange],
  );

  const onClear = React.useCallback(() => {
    if (withClear) {
      store.setValue('');
    }
  }, [store, withClear]);

  const { isPhone } = useDevices();

  return (
    <TextInput
      ref={inputRef}
      onFocus={handleFocus}
      onBlur={store.blur}
      size={isPhone ? 'md' : 'lg'}
      error={store.hasError && <AbsoluteError error={store.error} />}
      value={store.value ?? ''}
      onChange={onChange}
      disabled={store.disabled || disabled}
      withAsterisk={store.required}
      rightSection={
        withClear && store.value ? (
          withCloseButton ? (
            <CloseButton size="lg" onMouseDown={preventDefault} onClick={onClear} />
          ) : (
            <ActionIcon variant="transparent" color="grey" onClick={onClear}>
              <IconX size={16} />
            </ActionIcon>
          )
        ) : (
          rightSection
        )
      }
      styles={{
        input: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      }}
      {...props}
    />
  );
};

export default observer(FieldText);
